import { useMemo } from "react";

import { Flex } from "emoreg/layouts";
import { rem } from "polished";

import { WEEK_DAYS } from "./constants";
import { DatePickerStyle } from "./styles";
import { computeCalendarDays, dateObjectToString, getTodayString } from "./utils";

export type DatePickerProps = {
  /** month to display (format YYYY-MM) */
  targetMonth: string;
  /** classname, usable to overload component styling */
  className?: string;
  /** value (format YYYY-MM-DD) */
  startValue?: string | null;
  /** value (format YYYY-MM-DD) */
  endValue?: string | null;
  /** value change handler (only expose new value) */
  onSelect?: (value: string) => void;
  /** minimum selectable date (format YYYY-MM-DD) */
  min?: string;
  /** maximum selectable date (format YYYY-MM-DD) */
  max?: string;
};

export const DatePicker = ({
  className,
  targetMonth,
  startValue,
  endValue,
  onSelect,
  min,
  max,
}: DatePickerProps) => {
  const [year, month] = targetMonth.split("-").map(Number);
  // FIXME: remove useMemo with react 19
  const weeks = useMemo(() => computeCalendarDays(year, month), [year, month]);
  const today = getTodayString();

  const [start, end] = [startValue, endValue].sort();

  return (
    <Flex w={rem(254)} className={className} gap={4} direction="column">
      <Flex>
        {WEEK_DAYS.map(day => (
          <DatePickerStyle.HeaderDay key={day}>
            <span>{day.substring(0, 2)}</span>
          </DatePickerStyle.HeaderDay>
        ))}
      </Flex>
      {weeks.map((week, index) => (
        <Flex key={index} direction="row">
          {week.map(day => {
            const stringDate = dateObjectToString(day);
            const isStart = !!end && start === stringDate;
            const isEnd = !!start && end === stringDate;
            const isAfterStart = start && stringDate > start;
            const isBeforeEnd = end && stringDate < end;
            const isSelected = start === stringDate || end === stringDate;
            const isOutOfRange = (min && stringDate < min) || (max && stringDate > max);
            return (
              <DatePickerStyle.WeekDay
                key={stringDate}
                isPast={today > stringDate}
                isToday={today === stringDate}
                isTargetMonth={month === day.month}
                isSelected={isSelected}
                isSelectedStart={isStart}
                isSelectedEnd={isEnd}
                isInBetween={!!isAfterStart && !!isBeforeEnd}
                isDisabled={isOutOfRange}
                onClick={() => !isOutOfRange && onSelect?.(stringDate)}
              >
                {today === stringDate && <DatePickerStyle.TodayIndicator />}
                {day.day}
              </DatePickerStyle.WeekDay>
            );
          })}
        </Flex>
      ))}
    </Flex>
  );
};
